import { useMemo } from 'react';
import { generatePath, useLocation } from 'react-router-dom';
import {
  CaseSetupIcon,
  ClaimScoreIcon,
  ClassResolutionIcon,
  DashboardIcon,
  DeepValidationIcon,
  Flags,
  KeyOutlinedIcon,
  NavItem,
  NavItemProps,
  NavSubItem,
  SideNavDivider,
  SupervisorAccountIcon,
  useFlag,
  useTheme,
} from 'ui';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { DeveloperToolsTabs } from '@pages/DeveloperTools/DeveloperToolsContext';
import { PermissionsType } from '@shared/types/forms';
import { getOrgAndCaseId } from '@utils/getOrgAndCaseId';
import { PATHS, PRODUCT_BASE } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import {
  CLAIMSCORE_GROUP_NAV_BADGE,
  CLAIMSCORE_SETTINGS_NAV_BADGE,
} from './SideNavbar.testIds';

const DASHBOARD: NavItemProps = {
  label: 'Dashboard',
  icon: <DashboardIcon />,
  to: PATHS.PRIVATE_BASE.dashboard,
};

const CASE_SETUP: NavItemProps = {
  label: 'Case Setup',
  icon: <CaseSetupIcon />,
  to: PATHS.PRIVATE_BASE.caseSetup,
  activeUrl: PATHS.PRIVATE_BASE.caseSetup,
};

const TEAM: NavItemProps = {
  label: 'Team',
  icon: <SupervisorAccountIcon />,
  to: generatePath(PATHS.PRIVATE_BASE.permissions, {
    tab: PermissionsType.Organization,
  }),
  activeUrl: `${generatePath(PATHS.PRIVATE_BASE.permissions)}/`,
};

const DEV_TOOLS: NavItemProps = {
  label: 'Dev Tools',
  icon: <KeyOutlinedIcon />,
  to: generatePath(PATHS.PRIVATE_BASE.developerTools, {
    tab: DeveloperToolsTabs.API_KEYS,
  }),
};

const CLASS_RESOLUTION: NavItemProps = {
  label: 'ClassResolution',
  icon: <ClassResolutionIcon />,
  to: PATHS.PRIVATE_BASE.classResolution,
};

const DEEP_VALIDATION: NavItemProps = {
  label: 'DeepValidation',
  icon: <DeepValidationIcon />,
  to: PATHS.PRIVATE_BASE.deepValidation,
};

export function useMainSideNavbarNavItems() {
  const theme = useTheme();
  const { search } = useLocation();
  const {
    getUserRole,
    isCurrentCaseAdmin,
    isCurrentOrgAdmin,
    selectedCase,
    isLoading,
  } = useUserOrgCase();

  const isUploadClaimFilesEnabled = useFlag(Flags.uploadClaimsFile);

  const isDeveloper =
    getUserRole(RoleLevel.organization) === UserRole.Developer;

  const orgCaseQuery = getOrgAndCaseId(search);

  return useMemo(() => {
    const showWarning = !isLoading && !selectedCase?.isClaimscoreEnabled;

    const canSeeCaseSetup = Boolean(isCurrentCaseAdmin);
    const canSeePermissions = Boolean(isCurrentOrgAdmin || isCurrentCaseAdmin);
    const canSeeDevTools = Boolean(isCurrentOrgAdmin || isDeveloper);

    const claimScoreSubItems: NavSubItem[] = [
      {
        label: 'Uploaded Files',
        to: `${PATHS.PRIVATE_BASE.claimFiles}${orgCaseQuery}`,
        visible: Boolean(isUploadClaimFilesEnabled && isCurrentCaseAdmin),
      },
      {
        label: 'Result Reports',
        to: `${PATHS.PRIVATE_BASE.scheduledReports}${orgCaseQuery}`,
        visible: Boolean(isCurrentCaseAdmin),
      },
      {
        label: 'Settings',
        to: `${PATHS.PRIVATE_BASE.claimscoreSettings}${orgCaseQuery}`,
        visible: Boolean(isCurrentCaseAdmin),
        badge: showWarning ? 'warning' : undefined,
        badgeTestId: CLAIMSCORE_SETTINGS_NAV_BADGE,
      },
    ];

    const hasAdminItems =
      canSeeCaseSetup || canSeePermissions || canSeeDevTools;

    return (
      <>
        <NavItem
          {...DASHBOARD}
          kind="dark"
          to={`${DASHBOARD.to}${orgCaseQuery}`}
        />
        {hasAdminItems ? (
          <SideNavDivider bottomMargin light={false} topMargin />
        ) : null}
        <NavItem
          {...CASE_SETUP}
          kind="dark"
          to={`${CASE_SETUP.to}${orgCaseQuery}`}
          visible={canSeeCaseSetup}
        />
        <NavItem
          {...TEAM}
          kind="dark"
          to={`${TEAM.to}${orgCaseQuery}`}
          visible={canSeePermissions}
        />
        <NavItem
          {...DEV_TOOLS}
          kind="dark"
          to={`${DEV_TOOLS.to}${orgCaseQuery}`}
          visible={canSeeDevTools}
        />
        <SideNavDivider bottomMargin light={false} topMargin />
        <NavItem
          {...CLASS_RESOLUTION}
          activeColor={theme.palette.product.classresolution.main}
          activeSecondaryColor={theme.palette.product.classresolution.pastel}
          kind="dark"
          to={`${CLASS_RESOLUTION.to}${orgCaseQuery}`}
        />
        <NavItem
          {...DEEP_VALIDATION}
          activeColor={theme.palette.product.deepvalidation.main}
          activeSecondaryColor={theme.palette.product.deepvalidation.pastel}
          kind="dark"
          to={`${DEEP_VALIDATION.to}${orgCaseQuery}`}
        />
        <NavItem
          activeColor={theme.palette.product.claimscore.main}
          activePathPrefix={`${PRODUCT_BASE.claimscore}/`}
          activeSecondaryColor={theme.palette.product.claimscore.pastel}
          icon={<ClaimScoreIcon />}
          iconBadge={showWarning ? 'warning' : undefined}
          iconBadgeTestId={CLAIMSCORE_GROUP_NAV_BADGE}
          kind="dark"
          label="ClaimScore"
          subItems={claimScoreSubItems}
        />
      </>
    );
  }, [
    theme.palette.product,
    isLoading,
    selectedCase,
    orgCaseQuery,
    isCurrentOrgAdmin,
    isCurrentCaseAdmin,
    isUploadClaimFilesEnabled,
    isDeveloper,
  ]);
}
